import type {
  Attention,
  Evaluation,
  Forecast,
  ForecastSnapshot,
} from '../../adapters/forecast-http';
import type { EvidenceFrame, QuantifiedDetail } from '../../adapters/quantified-http';

export const metricName = (name: string) =>
  ({ WIND_SPEED: '风速', WIND_GUST: '阵风', TEMPERATURE: '温度', RELATIVE_HUMIDITY: '相对湿度' })[
    name
  ] ?? name;
export const reviewName = (state: string) =>
  ({ PENDING: '待核查', ACKNOWLEDGED: '已阅', REVIEWED: '已核查', DISMISSED: '已记录忽略' })[
    state
  ] ?? '人工状态未知';
export const eventName = (state: string) =>
  ({
    WEATHER_SIGNAL_TO_ATTENTION: '发现关注条件',
    INTERNAL_ATTENTION: '关注条件满足',
    RECOVERED: '条件已解除',
    EXPIRED: '证据有效期结束',
    SUSPENDED: '关注暂停判定',
    ACK: '人员已阅',
    REVIEW: '人员已核查',
    DISMISS: '人员记录忽略',
  })[state] ?? '关注记录更新';
export function uniqueAttentions(items: Attention[]) {
  const byId = new Map<string, Attention>();
  for (const a of items)
    if (!byId.has(a.id) || byId.get(a.id)!.version < a.version) byId.set(a.id, a);
  return [...byId.values()];
}
export function quantity(frame: EvidenceFrame | null | undefined) {
  const e = frame?.evaluation,
    r = e?.thresholdsFromApprovedRule;
  if (
    !e ||
    !r ||
    r.sourceMode !== 'DEMO' ||
    r.id !== e.ruleId ||
    r.version !== e.ruleVersion ||
    r.regionId !== e.regionId ||
    r.minimum === null ||
    !Number.isFinite(r.minimum)
  )
    return null;
  const m = e.metrics?.find((m) => m.name === r.metric && m.unit === r.unit);
  const source = frame?.forecasts.find(
    (f) =>
      e.forecastIds.includes(f.id) &&
      f.sourceMode === e.inputMode &&
      f.region.regionId === e.regionId &&
      f.metrics.some(
        (x) =>
          x.name === m?.name && x.unit === m.unit && x.value === m.value && x.quality === m.quality,
      ),
  );
  if (!m || m.value === null || !Number.isFinite(m.value) || !source) return null;
  return {
    metric: m,
    rule: r,
    source,
    actual: m.value,
    threshold: r.minimum,
    delta: m.value - r.minimum,
  };
}
export function currentCondition(
  a: Attention,
  frame: EvidenceFrame | null | undefined,
  now: number,
  unavailable = false,
) {
  const e = frame?.evaluation,
    q = quantity(frame);
  if (unavailable)
    return { state: 'UNKNOWN', label: '当前无法判断', reason: '证据服务暂不可用；历史事实仍保留' };
  if (
    !e ||
    !q ||
    e.id !== a.evaluationId ||
    e.inputMode !== a.inputMode ||
    e.regionId !== a.regionId ||
    (a.ruleVersion != null && e.ruleVersion !== a.ruleVersion)
  )
    return {
      state: 'UNKNOWN',
      label: '当前无法判断',
      reason: '该版本未保存完整可核对参数或证据身份不一致',
    };
  if (
    (e.result === 'ATTENTION' && a.state === 'RECOVERED') ||
    (e.result === 'NO_MATCH' && a.state === 'INTERNAL_ATTENTION')
  )
    return { state: 'UNKNOWN', label: '证据与状态待核实', reason: 'EVIDENCE_STATE_CONFLICT' };
  if (
    ![e.qualityGate, e.spatialGate, e.temporalGate].every((x) => x === 'PASS') ||
    !['DEMO_FIXTURE', 'PROVIDER_FORECAST'].includes(q.metric.quality) ||
    !['DEMO_FIXTURE', 'PROVIDER_FORECAST'].includes(q.source.quality) ||
    Date.parse(e.validUntil) <= now ||
    Date.parse(q.source.validUntil) <= now ||
    Date.parse(q.source.issuedAt) + q.rule.maxAgeSeconds * 1000 <= now ||
    Date.parse(q.source.retrievedAt) + q.rule.maxAgeSeconds * 1000 <= now ||
    Date.parse(e.evaluatedAt) > now
  )
    return {
      state: 'UNKNOWN',
      label: '当前无法判断',
      reason: '数据过期、资格不合格或时间未知；历史解除不代表当前安全',
    };
  if (e.result === 'ATTENTION')
    return { state: 'MATCHED', label: '关注条件仍满足', reason: `截至 ${e.evaluatedAt}` };
  if (e.result === 'NO_MATCH')
    return {
      state: 'RECOVERED',
      label: '条件已解除',
      reason: `截至 ${e.evaluatedAt}，仅表示演示规则条件变化`,
    };
  return { state: 'UNKNOWN', label: '当前无法判断', reason: '评价证据不足或规则未批准' };
}
export function currentFrame(data: ForecastSnapshot | null, a: Attention): EvidenceFrame | null {
  const e = data?.evaluations.find(
    (e) => e.id === a.evaluationId && e.regionId === a.regionId && e.inputMode === a.inputMode,
  );
  return e
    ? { evaluation: e, forecasts: data!.forecasts.filter((f) => e.forecastIds.includes(f.id)) }
    : null;
}
export interface TrendPoint {
  key: string;
  evaluation: Evaluation;
  source: Forecast;
  time: number;
  value: number | null;
  unit: string;
  metric: string;
  qualified: boolean;
  group: string;
}
export function trendPoints(detail: QuantifiedDetail): TrendPoint[] {
  const points: TrendPoint[] = [];
  const seen = new Set<string>();
  for (const frame of detail.series) {
    const e = frame.evaluation,
      r = e.thresholdsFromApprovedRule;
    if (!r) continue;
    for (const f of frame.forecasts) {
      const m = f.metrics.find((m) => m.name === r.metric && m.unit === r.unit);
      const key = `${f.id}:${r.metric}`;
      if (!m || seen.has(key)) continue;
      seen.add(key);
      points.push({
        key,
        evaluation: e,
        source: f,
        time: Date.parse(f.validFrom),
        value: m.value,
        unit: m.unit,
        metric: m.name,
        qualified:
          [e.qualityGate, e.spatialGate, e.temporalGate].every((x) => x === 'PASS') &&
          m.quality === 'DEMO_FIXTURE' &&
          f.quality === 'DEMO_FIXTURE' &&
          m.value !== null &&
          Number.isFinite(m.value),
        group: [
          f.region.sourceId,
          f.provider,
          f.product,
          f.sourceMode,
          f.issuedAt,
          f.revision,
          m.name,
          m.unit,
          r.version,
        ].join('|'),
      });
    }
  }
  return points.sort(
    (a, b) =>
      a.time - b.time ||
      a.source.issuedAt.localeCompare(b.source.issuedAt) ||
      a.key.localeCompare(b.key),
  );
}
/** Connect only adjacent, qualified periods from the very same issue/revision. */
export function trendSegments(points: TrendPoint[]) {
  return points.flatMap((p, i) => {
    const previous = points[i - 1];
    return previous &&
      p.qualified &&
      previous.qualified &&
      p.group === previous.group &&
      p.time > previous.time &&
      Date.parse(previous.source.validUntil) === p.time
      ? [[previous, p] as const]
      : [];
  });
}
